import { differenceBy } from "lodash-es";
import percentRound from "percent-round";
import { To } from "react-router-dom";
import { BEBOP_ROUTES, MAX_SELECTABLE_TOKENS } from "../config/consts";
import {
  GlobalStore,
  INITIAL_STATE,
  MakerTokens,
  SelectedToken,
} from "../store/global/GlobalStore";
import { TokenList } from "../store/global/getAllTokenLists";
import { Amount } from "../types/amount";
import { SUPPORTED_CHAINS } from "config/chains";
import { getAddress, Hash } from "viem";
import { Chain } from "@rainbow-me/rainbowkit";

export const URL_KEYS = {
  NETWORK: "network",
  SELL: "sell",
  BUY: "buy",
  SELL_AMOUNT: "sellAmounts",
};
const URL_KEY_ORDER = Object.values(URL_KEYS).reduce(
  (prev, key, i) => {
    prev[key] = i;
    return prev;
  },
  {} as Record<string, number>
);

export const isValidToken = (maybeToken: string, tokenList: TokenList): boolean => {
  //Two cases maybeToken is a token address or a token name
  const tokenIsAddress = maybeToken.startsWith("0x");
  if (tokenIsAddress) {
    let checkSummedAddress: Hash | undefined;
    try {
      checkSummedAddress = getAddress(maybeToken);
    } catch (e) {
      return false;
    }
    const tokenIsApplicableToNetwork = tokenList[checkSummedAddress] !== undefined;
    return tokenIsApplicableToNetwork;
  }
  const match = Object.values(tokenList).find(token => token.ticker === maybeToken);
  const tokenIsApplicableToNetwork = match !== undefined;
  return tokenIsApplicableToNetwork;
};
const isTokenAddress = (token: string) => token.startsWith("0x");

export const processMakerTokensFromURL = (
  queryParam: string,
  tokenList: TokenList
): MakerTokens => {
  const entries = queryParam.split(",");
  const validEntries = entries
    .filter(entry => isValidToken(entry, tokenList))
    .map(e => {
      let tokenAddress: Hash | undefined;
      // Checksum the address before returning it
      if (isTokenAddress(e)) {
        try {
          tokenAddress = getAddress(e);
          return tokenAddress;
        } catch (e) {
          return undefined;
        }
      }
      return getTokenAddressByTicker(e, tokenList);
    })
    .filter(e => e !== undefined);
  if (validEntries.length === 0) {
    updateUrlParams(URL_KEYS.BUY, "", window.location.pathname);
  }

  const slicedEntries = validEntries.slice(0, MAX_SELECTABLE_TOKENS);
  const makerTokens = slicedEntries.map((entry, index, arr) => {
    return {
      tokenAddress: entry as Hash,
      amount: Amount.ZERO,
      pct: 100 / arr.length,
      isLocked: false,
    };
  });
  const roundedPercentages = percentRound(makerTokens.map(entry => entry.pct));
  return makerTokens.map((entry, index) => {
    entry.pct = roundedPercentages[index];
    return entry;
  });
};

export const processTakerTokensFromURL = (
  tokenQueryParam: string,
  amountQueryParam: string,
  tokenList: TokenList
): Array<SelectedToken> => {
  const fromEntries = tokenQueryParam.split(",");
  const amountEntries = amountQueryParam.split(",");
  // Parse the amounts
  const amounts = amountEntries.map(entry => {
    if (entry && entry !== "" && !isNaN(Number(entry))) {
      return new Amount(entry);
    }
    return Amount.ZERO;
  });

  return (
    fromEntries
      .slice(0, MAX_SELECTABLE_TOKENS)
      .filter(token => isValidToken(token, tokenList))
      .map(e => {
        let tokenAddress: Hash | undefined;
        // Checksum the address before returning it
        if (isTokenAddress(e)) {
          try {
            tokenAddress = getAddress(e);
            return tokenAddress;
          } catch (e) {
            return undefined;
          }
        }
        return getTokenAddressByTicker(e, tokenList);
      })
      .filter(e => e !== undefined) as Array<Hash>
  ).map((tokenAddress, index) => {
    return {
      tokenAddress: tokenAddress,
      amount: amounts[index] ?? Amount.ZERO,
    };
  });
};

const getTokenAddressByTicker = (ticker: string, tokenList: TokenList): Hash | undefined => {
  const tokenMatches = Object.values(tokenList).filter(token => token.ticker === ticker);
  if (tokenMatches.length === 1) {
    return tokenMatches[0].chainInfo.contractAddress as Hash;
  }
  return undefined;
};

export const processNetworkFromUrl = (networkQueryParam: string | null): Chain | undefined => {
  const potentialMatch = SUPPORTED_CHAINS.find(chain => {
    return chain.name.toLowerCase() === networkQueryParam?.toLowerCase();
  });
  return potentialMatch;
};
export const processTokensFromUrl = (
  sellTokens: string | null,
  buyTokens: string | null,
  amounts: string | null,
  tokenList: TokenList
): void => {
  // Check if sellTokens and buyTokens contain the same token
  const sellTokensArray = sellTokens?.split(",");
  const buyTokensArray = buyTokens?.split(",");
  const intersection = sellTokensArray?.filter(token => buyTokensArray?.includes(token));
  if (intersection && intersection.length > 0) {
    // If there is an intersection (i.e. duplicate tokens in buy/sell, we should clear the URL params
    updateUrlParams(URL_KEYS.SELL, "", window.location.pathname);
    updateUrlParams(URL_KEYS.BUY, "", window.location.pathname);
    return;
  }
  GlobalStore.update(s => {
    const takerTokens = processTakerTokensFromURL(sellTokens ?? "", amounts ?? "", tokenList);
    const makerTokens = processMakerTokensFromURL(buyTokens ?? "", tokenList);
    // If we navigate back to OrderEntry from Order Review - we shouldn't recreate the tokens
    const changedMakerTokens = differenceBy(makerTokens, s.makerTokens, "token");
    const changedTakerTokens = differenceBy(takerTokens, s.takerTokens, "token");
    // Protect against many-to-many edge case.
    const isManyToMany = takerTokens.length > 1 && makerTokens.length > 1;
    if (changedTakerTokens.length > 0) {
      s.takerTokens = takerTokens;
    }
    if (changedMakerTokens.length > 0) {
      s.makerTokens = !isManyToMany ? makerTokens : [makerTokens[0]];
    }
  });
};

/**
 * Updates the query params on the URL without reloading the page.
 * @param key
 * @param value
 * @param pathname
 */
export const updateUrlParams = (key: string, value: string, pathname: string): void => {
  if ((pathname as BEBOP_ROUTES) !== BEBOP_ROUTES.Trade) {
    return;
  }
  // Sort searchParams
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete(key);
  const sortedSearchParams = new URLSearchParams(
    [...searchParams.entries(), [key, value]].sort((a, b) => {
      const [aKey] = a;
      const [bKey] = b;
      return URL_KEY_ORDER[aKey] - URL_KEY_ORDER[bKey];
    })
  );
  console.log(`Updating URL PARAMS`);
  replaceUrlWithNewParams(sortedSearchParams);
};

export const removeUrlParam = (key: string): void => {
  const params = new URLSearchParams(window.location.search);
  params.delete(key);
  replaceUrlWithNewParams(params);
};

const replaceUrlWithNewParams = (searchParams: URLSearchParams) => {
  const newUrl =
    window.location.protocol +
    "//" +
    window.location.host +
    window.location.pathname +
    "?" +
    searchParams.toString();
  try {
    window.history.replaceState({ path: newUrl }, "", newUrl);
  } catch (e) {
    console.error(e);
  }
};

export const initURLParams = () => {
  // We only init URL params if there aren't any already - to avoid overriding user's desired state
  const params = new URLSearchParams(window.location.search);
  if (params.get(URL_KEYS.NETWORK)) {
    return;
  }
  updateUrlParams(
    URL_KEYS.NETWORK,
    INITIAL_STATE.network?.name.toLowerCase() ?? "",
    window.location.pathname
  );
};

export const isInternalPath = (to: To) => {
  if (typeof to === "string" && to.startsWith("/")) {
    return true;
  }

  const currentUrl = new URL(window.location.href);
  let toString = undefined;

  if (typeof to === "string") {
    toString = to;
  } else {
    if (to.pathname != null) {
      toString = to.pathname + (to.search ?? "") + (to.hash ?? "");
    }
  }
  if (toString == null) {
    console.warn("Not a valid pathname");
    return false;
  }
  const targetUrl = toString.startsWith("//")
    ? new URL(currentUrl.protocol + toString)
    : new URL(toString);
  return targetUrl.origin === currentUrl.origin;
};
